import { useTranslation } from 'react-i18next';
import { PartnerPartnerDetails, SubPartnerAccount } from 'vopay';
import { UtilButton } from 'UI';
import EntityHeaderCard from '@/components/Entities/Accounts/AccountHeaderCards/EntityHeaderCard';
import type { AccountCredentials } from '@/components/Entities/Accounts/AccountHeaderCards/accountCredentials';
import ActiveIcon from '@/components/Partners/PartnerDetails/svg/Active.svg';
import PendingIcon from '@/components/Partners/PartnerDetails/svg/Pending.svg';

type PartnerCardProps = {
  partner: SubPartnerAccount;
  /** Null hides the credentials strip — the caller decides who may see it and when (Active only). */
  credentials: AccountCredentials | null;
  /** A placeholder stands in for the strip while the credentials are still on their way. */
  isLoadingCredentials: boolean;
  /** The fetched agreement status, once it has arrived; shown while the partner is Agreement Pending. */
  agreement: PartnerPartnerDetails['Agreement'] | undefined;
  /**
   * The View Permissions link under the name (spec 5.5 / 5.7), which opens the Partner Permissions
   * drawer. The list's quick-view drawer passes it; the partner page has a Permissions tab instead.
   */
  onViewPermissionsClick?: () => void;
};

/**
 * The partner account's identity card (Figma "Heading Card", spec 5.5): name, currency and status,
 * with the API credentials strip along the bottom when the caller passes credentials. A partner
 * account has no balance or limits of its own, so this is the drawer's only header card.
 */
export default function PartnerCard({
  partner,
  credentials,
  isLoadingCredentials,
  agreement,
  onViewPermissionsClick,
}: PartnerCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerCard' });

  // Spec 5.5: while the agreement is what the partner account is waiting on, say where it stands.
  // The copy-link and send-by-email actions need endpoints that do not exist yet (ID-13825).
  const agreementNote =
    partner.AccountStatus === 'Agreement Pending' && agreement
      ? agreement.DocumentCompleted
        ? t('agreementSigned', { defaultValue: 'Agreement signed' })
        : agreement.DocumentSent
          ? t('agreementSent', { defaultValue: 'Agreement sent, awaiting signature' })
          : t('agreementNotSent', { defaultValue: 'Agreement not sent yet' })
      : undefined;

  return (
    <EntityHeaderCard
      data-testid="partnerCard"
      icon={partner.IsActive ? ActiveIcon : PendingIcon}
      iconAlt={t(partner.IsActive ? 'iconAltActive' : 'iconAltPending', {
        defaultValue: partner.IsActive ? 'Active partner' : 'Pending partner',
      })}
      // Figma: H2/Large Title 28/Emphasized over Body/16/Regular in Text/Secondary75
      name={partner.Name}
      nameLevel="2"
      subtitle={partner.Currency}
      belowName={
        onViewPermissionsClick && (
          <UtilButton size="medium" iconRight="chevron_right" onClick={onViewPermissionsClick}>
            {t('viewPermissions', { defaultValue: 'View Permissions' })}
          </UtilButton>
        )
      }
      status={partner}
      statusNote={agreementNote}
      credentials={credentials}
      isLoadingCredentials={isLoadingCredentials}
    />
  );
}
